import { usePopover } from '@/hooks/usePopover';
import { useToast } from '@/hooks/useToast';
import { DownloadFormat, downloadSpreadsheet } from '@/utils/downloadUtils';
import { getFilename } from '@/utils/path';
import { FileDownloadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';
import { Divider, IconButton, ListItemIcon, Tooltip, useTheme } from '@mui/material';
import { useCallback, useState } from 'react';
import { StyledList, StyledListHeader, StyledListItem } from '../AssetsPage/PopoverList';
import { BodyText } from '../BodyText';
import { RoundedPopover } from '../Popover/RoundedPopover';
import { SearchType } from './types';
import { MatchAsset } from './SearchTable';
import { capitalize } from '@/utils/capitalize';

export const formatSearchResultsFilename = (
  searchAssetPath: string | undefined,
  searchType: SearchType | undefined,
  format: DownloadFormat
): string => {
  const capitalizedSearchType = searchType ? capitalize(searchType) : undefined;
  let baseName = 'Search Results';
  if (searchAssetPath) {
    const assetFilename = getFilename(searchAssetPath);
    const lastDotIndex = assetFilename.lastIndexOf('.');
    const assetNameWithoutExtension = lastDotIndex === -1 ? assetFilename : assetFilename.slice(0, lastDotIndex);
    // eslint-disable-next-line unicorn/prefer-string-replace-all -- replaceAll requires ES2021, but TypeScript target is ES2020
    const sanitizedAssetName = assetNameWithoutExtension.replace(/[\\/:*?"<>|]/g, '_');
    baseName = `${sanitizedAssetName} - ${capitalizedSearchType || 'Search'} Search Results`;
  } else if (capitalizedSearchType) {
    baseName = `${capitalizedSearchType} Search Results`;
  }
  const today = new Date().toISOString().split('T')[0];
  return `${baseName} ${today}.${format}`;
};

export const SearchResultsDownload = ({
  fetchAllResults,
  disabled,
  searchAssetPath,
  searchAssetId,
  searchQuery,
  searchType,
}: {
  fetchAllResults: () => Promise<MatchAsset[]>;
  disabled?: boolean;
  searchAssetPath?: string;
  searchAssetId?: string;
  searchQuery?: string;
  searchType?: SearchType;
}) => {
  const { showToast } = useToast();
  const theme = useTheme();
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    open: openDownload,
    close: closeDownload,
    isOpen: isDownloadOpen,
    anchorElement: downloadAnchorElement,
  } = usePopover();

  const handleDownload = useCallback(
    async (format: DownloadFormat) => {
      try {
        setIsDownloading(true);
        const results = await fetchAllResults();

        if (results.length === 0) {
          showToast({ message: 'No search results to download', type: 'info' });
          return;
        }

        const isPartSearch = searchType === 'part';
        const isGeometricSearch = searchType === 'geometric';
        const isAssetSearch = !!searchAssetPath && !!searchAssetId;
        const hasSearchQuery = !!searchQuery;

        let headers: string[];
        let rows: string[][];

        const contextHeaders: string[] = [];
        const contextCell: string[] = [];
        if (isAssetSearch) {
          contextHeaders.push('Asset Path', 'Asset ID');
          contextCell.push(searchAssetPath, searchAssetId);
        }
        if (hasSearchQuery) {
          contextHeaders.push('Search Query');
          contextCell.push(searchQuery);
        }

        if (isPartSearch) {
          headers = [...contextHeaders, '% in Match', '% of Match', 'Match Path', 'Match ID'];
          rows = results.map(matchAsset => [
            ...contextCell,
            matchAsset.forwardMatchPercentage?.toString() || '',
            matchAsset.reverseMatchPercentage?.toString() || '',
            matchAsset.path || '',
            matchAsset.id || '',
          ]);
        } else if (isGeometricSearch) {
          headers = [...contextHeaders, 'Match %', 'MatchPath', 'Match ID'];
          rows = results.map(matchAsset => {
            return [
              ...contextCell,
              matchAsset.matchPercentage?.toString() || '',
              matchAsset.path || '',
              matchAsset.id || '',
            ];
          });
        } else {
          headers = [...contextHeaders, 'Match Path', 'Match ID'];
          rows = results.map(matchAsset => {
            return [...contextCell, matchAsset.path || '', matchAsset.id || ''];
          });
        }

        const data = [headers, ...rows];

        const filename = formatSearchResultsFilename(searchAssetPath, searchType, format);
        downloadSpreadsheet(data, { filename, format });
        showToast({ message: `Downloaded ${results.length} results as ${format.toUpperCase()}`, type: 'success' });
      } catch {
        showToast({ message: `Failed to download ${format.toUpperCase()}`, type: 'error' });
      } finally {
        setIsDownloading(false);
        closeDownload();
      }
    },
    [fetchAllResults, showToast, searchAssetPath, searchAssetId, searchQuery, searchType, closeDownload]
  );

  const formats: DownloadFormat[] = ['csv', 'xlsx'];
  const formatLabels: Record<DownloadFormat, string> = {
    csv: 'CSV',
    xlsx: 'XLSX',
    xls: 'XLS',
  };

  return (
    <>
      <Tooltip title='Download Search Results' placement='bottom'>
        <span>
          <IconButton
            size='small'
            onClick={openDownload}
            disabled={disabled || isDownloading}
            sx={{
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <FileDownloadOutlined fontSize='small' />
          </IconButton>
        </span>
      </Tooltip>
      <RoundedPopover
        open={isDownloadOpen}
        anchorEl={downloadAnchorElement}
        onClose={closeDownload}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        slotProps={{
          paper: {
            sx: {
              marginTop: '4px',
              borderRadius: '12px',
            },
          },
        }}
      >
        <StyledList>
          <StyledListHeader>
            <BodyText>Download Search Results</BodyText>
          </StyledListHeader>
          <Divider />
          {formats.map(format => (
            <StyledListItem
              key={format}
              onClick={() => void handleDownload(format)}
              sx={{ cursor: 'pointer' }}
              data-testid={`search-results-download-${format}-option`}
            >
              <ListItemIcon>
                <InsertDriveFileOutlined color='action' sx={{ fontSize: '18px' }} />
              </ListItemIcon>
              <BodyText>{formatLabels[format]}</BodyText>
            </StyledListItem>
          ))}
        </StyledList>
      </RoundedPopover>
    </>
  );
};
